import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

export type FtsMode = 'or' | 'and' | 'exact';

export interface FilterState {
  searchQuery: string;
  searchMode: 'keyword' | 'semantic';
  ftsMode: FtsMode;
  selectedTags: string[];
  selectedStatuses: string[];
  selectedConnectionTypes: string[];
}

export interface FilterContextValue extends FilterState {
  setSearchQuery: (q: string) => void;
  setSearchMode: (m: 'keyword' | 'semantic') => void;
  setFtsMode: (m: FtsMode) => void;
  setSelectedTags: (tags: string[]) => void;
  toggleTag: (tag: string) => void;
  removeTag: (tag: string) => void;
  setSelectedStatuses: (statuses: string[]) => void;
  toggleStatus: (status: string) => void;
  toggleConnectionType: (type: string) => void;
  clearFilters: () => void;
  activeFilterCount: number;
}

const FilterContext = createContext<FilterContextValue | null>(null);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'keyword' | 'semantic'>('semantic');
  const [ftsMode, setFtsMode] = useState<FtsMode>('or');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedConnectionTypes, setSelectedConnectionTypes] = useState<string[]>([]);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }, []);

  const removeTag = useCallback((tag: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tag));
  }, []);

  const toggleStatus = useCallback((status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  }, []);

  const toggleConnectionType = useCallback((type: string) => {
    setSelectedConnectionTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedTags([]);
    setSelectedStatuses([]);
    setSelectedConnectionTypes([]);
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery.trim()) count++;
    count += selectedTags.length;
    count += selectedStatuses.length;
    count += selectedConnectionTypes.length;
    return count;
  }, [searchQuery, selectedTags, selectedStatuses, selectedConnectionTypes]);

  const value = useMemo<FilterContextValue>(
    () => ({
      searchQuery,
      searchMode,
      ftsMode,
      selectedTags,
      selectedStatuses,
      selectedConnectionTypes,
      setSearchQuery,
      setSearchMode,
      setFtsMode,
      setSelectedTags,
      toggleTag,
      removeTag,
      setSelectedStatuses,
      toggleStatus,
      toggleConnectionType,
      clearFilters,
      activeFilterCount,
    }),
    [
      searchQuery, searchMode, ftsMode,
      selectedTags, selectedStatuses, selectedConnectionTypes,
      toggleTag, removeTag, toggleStatus, toggleConnectionType, clearFilters, activeFilterCount,
    ]
  );

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within a FilterProvider');
  return ctx;
}
